import { useEffect, useMemo, useState } from "react";
import { Button, Col, Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import { api_without_cancellation } from "../../api/api";
import {
  formatCalendarDateForInput,
  formatPhoneNumber,
  getCaseId,
  getClientId,
} from "../../Utils/helper";
import InsuranceDetailsFields from "../DefendantPage/DefandantMain/modals/fields/insurance/InsuranceDetailsFields";
import InsuranceCompanyFields from "../DefendantPage/DefandantMain/modals/fields/insurance/InsuranceCompanyFields";
import InsuranceAdjusterFields from "../DefendantPage/DefandantMain/modals/fields/insurance/InsuranceAdjusterFields";
import InsuranceSupervisorFields from "../DefendantPage/DefandantMain/modals/fields/insurance/InsuranceSupervisorFields";
import CounselSupervisingAttorneyFields from "../DefendantPage/DefandantMain/modals/fields/counsel/CounselSupervisingAttorneyFields";
import CounselFileInformationFields from "../DefendantPage/DefandantMain/modals/fields/counsel/CounselFileInformationFields";
import CounselFirmFields from "../DefendantPage/DefandantMain/modals/fields/counsel/CounselFirmFields";
import CounselAttorneyFields from "../DefendantPage/DefandantMain/modals/fields/counsel/CounselAttorneyFields";
import InsuranceLienAdjusterFields from "../DefendantPage/DefandantMain/modals/fields/insurance/InsuranceLienAdjusterFields";
import InsuranceLienInformationFields from "../DefendantPage/DefandantMain/modals/fields/insurance/InsuranceLienInformationFields";

import PartyInformationFields from "./fields/PartyInformationFields";
import PartyContactFields from "./fields/PartyContactFields";
import PartyEmployerFields from "./fields/PartyEmployerFields";

const TabGrid = ({ tabs, selectedId, onSelect, onDelete, onAdd }) => (
  <div className="w-100 m-t-5 p-l-5 p-r-5" style={{ minHeight: "25px" }}>
    <div
      className="overflow-scroll invisible-scrollbar"
      style={{
        display: "grid",
        gridTemplateRows: `repeat(${Math.ceil(((tabs?.length ?? 0) + (onAdd ? 1 : 0)) / 4)}, 1fr)`,
        gridAutoFlow: "column",
        gap: "5px",
      }}
    >
      {tabs?.map((item) => {
        const isSelected = selectedId === item.id;
        return (
          <div key={item.id} style={{ position: "relative", display: "flex" }}>
            <button
              type="button"
              className={`event-type-button btn-hover-event-litigation-act height-25 ${isSelected ? "selected" : "unselected"}`}
              onClick={() => onSelect(item.id)}
              style={{ flex: 1, paddingRight: onDelete ? "5px" : undefined }}
            >
              <span className="text-nowrap">{item.label}</span>
            </button>
            {onDelete && (
              <span
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item.id);
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "0px",
                  color: isSelected ? "white" : "black",
                  fontSize: 14,
                  cursor: "pointer",
                }}
                title="Remove"
              >
                ×
              </span>
            )}
          </div>
        );
      })}
      {onAdd && (
        <button
          type="button"
          className="event-type-button height-25 unselected"
          onClick={onAdd}
          title="Add another"
        >
          <span className="text-nowrap">Add Another</span>
        </button>
      )}
    </div>
  </div>
);

// ── FormSection: routes to the right fieldset ──────────────────────────────
const FormSection = ({ firstTab, secondTab, entityId, statesAbrs }) => {
  if (firstTab === "party") {
    if (secondTab === "information") return <PartyInformationFields />;
    if (secondTab === "contact") return <PartyContactFields statesAbrs={statesAbrs} />;
    if (secondTab === "employer") return <PartyEmployerFields statesAbrs={statesAbrs} />;
  }
  // Insurance (collection — entityId required)
  if (firstTab === "insurance") {
    if (secondTab === "insurance") return <InsuranceDetailsFields entityId={entityId} />;
    if (secondTab === "company")
      return <InsuranceCompanyFields entityId={entityId} stateAbrs={statesAbrs} />;
    if (secondTab === "adjuster")
      return <InsuranceAdjusterFields entityId={entityId} stateAbrs={statesAbrs} />;
    if (secondTab === "supervisor")
      return <InsuranceSupervisorFields entityId={entityId} stateAbrs={statesAbrs} />;
    if (secondTab === "lien_adjuster")
      return <InsuranceLienAdjusterFields entityId={entityId} stateAbrs={statesAbrs} />;
    if (secondTab === "lien_information")
      return <InsuranceLienInformationFields entityId={entityId} stateAbrs={statesAbrs} />;
  }

  // Counsel (collection)
  if (firstTab === "counsel") {
    if (secondTab === "fileInformation") return <CounselFileInformationFields entityId={1} />;
    if (secondTab === "firm") return <CounselFirmFields entityId={1} statesData={statesAbrs} />;
    if (secondTab === "attorney")
      return <CounselAttorneyFields entityId={1} statesData={statesAbrs} />;
    if (secondTab === "supervisingAttorney")
      return <CounselSupervisingAttorneyFields entityId={1} statesData={statesAbrs} />;
  }

  return null;
};

const VALIDATION_RULES = [
  {
    collection: "counsel",
    requiredField: "counsel_type_id",
    firstTab: "counsel",
    secondTab: "fileInformation",
    message: "Counsel type is required",
  },
  {
    collection: "insurance",
    requiredField: "insurance_type_id",
    firstTab: "insurance",
    secondTab: "insurance",
    message: "Insurance type is required",
  },
];
// ── Static config ──────────────────────────────────────────────────────────
const FIRST_TABS = [
  { id: "party", label: "Party" },
  { id: "insurance", label: "Insurance" },
  //   { id: "service", label: "Service" },
  { id: "counsel", label: "Counsel" },
];

const SECOND_TABS = {
  party: [
    { id: "contact", label: "Contact" },
    { id: "information", label: "Information" },
    { id: "employer", label: "Employer" },
  ],
  insurance: [
    { id: "company", label: "Insurance Company" },
    { id: "insurance", label: "Claim Information" },
    { id: "adjuster", label: "Adjuster" },
    { id: "lien_adjuster", label: "Lien Adjuster" },
    { id: "supervisor", label: "Supervisor" },
    { id: "lien_information", label: "Lien Information" },
  ],
  counsel: [
    { id: "fileInformation", label: "File Information" },
    { id: "firm", label: "Firm" },
    { id: "attorney", label: "Attorney" },
    { id: "supervisingAttorney", label: "Supervising Attorney" },
  ],
};

// Which top tabs support multiple entries. Defendant is singular.
// const COLLECTION_TABS = ["insurance", "service", "counsel"];
const COLLECTION_TABS = ["insurance"];
const LABEL = { insurance: "Insurance", service: "Service", counsel: "Counsel" };

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const newEntity = () => ({ id: uid(), data: {} });

const AddOtherPartiesModal = ({
  show,
  handleClose,
  refetch,
  isDummy = false,
  isEdit,
  defendant = {},
  deleteConfirmPopup,
}) => {
  const PARTYDEFAULTS = {
    // Information
    party_type_id: 1,
    ssn: "",
    gender: "",
    liability_percent: "",
    liability_estimate: "",
    repr_letter_sent: null,
    contact_date: null,
    party_served_date: null,

    // Contact
    first_name: "",
    middle_name: "",
    last_name: "",
    home_address1: "",
    home_address2: "",
    home_city: "",
    home_state: "",
    home_zip: "",
    home_phone: "",
    home_extension: "",
    home_fax: "",
    home_email: "",
    home_website: "",
    birthday: null,

    // Employer
    party_employer: "",
    work_address1: "",
    work_address2: "",
    work_city: "",
    work_state: "",
    work_zip: "",
    work_phone: "",
    work_extension: "",
    work_fax: "",
    work_email: "",
    work_website: "",

    is_blocked: false,
  };
  // Put these near your other constants in AddDefendantModal

  const initialEntities = useMemo(
    () => ({
      insurance: [newEntity()],
      service: [newEntity()],
      counsel: [newEntity()],
    }),
    []
  );

  const methods = useForm({
    defaultValues: {
      ...PARTYDEFAULTS,
      is_blocked: false,
    },
  });

  const { register, setValue, handleSubmit, reset } = methods;
  const [firstTab, setFirstTab] = useState("party");
  const [secondTab, setSecondTab] = useState(SECOND_TABS.party[0].id);
  const [loading, setLoading] = useState(false);

  const [entities, setEntities] = useState(initialEntities);
  const [activeEntityId, setActiveEntityId] = useState({
    insurance: initialEntities.insurance[0].id,
    service: initialEntities.service[0].id,
    counsel: initialEntities.counsel[0].id,
  });

  // Reset second tab when first tab changes
  useEffect(() => {
    setSecondTab(SECOND_TABS[firstTab][0].id);
  }, [firstTab]);

  const isCollection = COLLECTION_TABS.includes(firstTab);
  const currentList = isCollection ? entities[firstTab] : null;
  const currentEntityId = isCollection ? activeEntityId[firstTab] : null;
  const addEntity = () => {
    const entity = newEntity();
    setEntities((prev) => ({ ...prev, [firstTab]: [...prev[firstTab], entity] }));
    setActiveEntityId((prev) => ({ ...prev, [firstTab]: entity.id }));
  };
  const deleteEntity = (idToDelete) => {
    setEntities((prev) => {
      const list = prev[firstTab];
      // Always keep at least one entry — replace with a fresh blank instead
      if (list.length === 1) {
        const fresh = newEntity();
        setActiveEntityId((p) => ({ ...p, [firstTab]: fresh.id }));
        return { ...prev, [firstTab]: [fresh] };
      }
      const next = list.filter((e) => e.id !== idToDelete);
      // If the deleted one was active, pick a neighbor
      if (idToDelete === activeEntityId[firstTab]) {
        const deletedIdx = list.findIndex((e) => e.id === idToDelete);
        const fallback = next[deletedIdx] ?? next[deletedIdx - 1] ?? next[0];
        setActiveEntityId((p) => ({ ...p, [firstTab]: fallback.id }));
      }
      return { ...prev, [firstTab]: next };
    });
  };

  const selectEntity = (id) => {
    setActiveEntityId((prev) => ({ ...prev, [firstTab]: id }));
  };
  const thirdTabs = useMemo(() => {
    if (!currentList) return [];
    return currentList.map((entity, idx) => ({
      id: entity.id,
      label: `${LABEL[firstTab]} ${idx + 1}`,
    }));
  }, [currentList, firstTab]);

  const [statesAbrs, setStatesAbrs] = useState([]); //state abrs

  const fetchStatesData = async () => {
    try {
      const response = await api_without_cancellation.get("/api/states/");
      if (response.status === 200) {
        setStatesAbrs(response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchStatesData();
  }, []);

  useEffect(() => {
    if (show && isDummy) {
      setValue("is_blocked", isDummy);
    }
  }, [show, isDummy]);
  const isNonEmpty = (obj) =>
    obj &&
    Object.values(obj).some((v) => {
      if (v === null || v === undefined) return false;
      if (typeof v === "string") return v.trim() !== "";
      if (typeof v === "object") return Object.values(v).some(Boolean);
      return true;
    });
  const buildPayload = (values) => {
    const {
      is_blocked,
      insurance: insuranceMap = {},
      counsel: counselMap = {},
      ...defendantFields
    } = values;

    if (is_blocked) {
      return {
        is_blocked: true,
        ...defendantFields,
        insurances: Object.values(insuranceMap).filter(isNonEmpty),
        counsels: Object.values(counselMap).filter(isNonEmpty),
      };
    }

    return {
      is_blocked: false,
      ...defendantFields,
      insurances: Object.values(insuranceMap).filter(isNonEmpty),
      counsels: Object.values(counselMap).filter(isNonEmpty),
    };
  };
  const onSubmit = async (values) => {
    for (const rule of VALIDATION_RULES) {
      const map = values[rule.collection] ?? {};
      const invalid = Object.entries(map).find(([, data]) => {
        if (!isNonEmpty(data)) return false;
        return !data[rule.requiredField];
      });

      if (invalid) {
        const [badId] = invalid;
        setFirstTab(rule.firstTab);
        setSecondTab(rule.secondTab);
        setActiveEntityId((prev) => ({ ...prev, [rule.collection]: badId }));
        setTimeout(() => {
          methods.setError(`${rule.collection}.${badId}.${rule.requiredField}`, {
            type: "required",
            message: rule.message,
          });
        }, 0);
        return;
      }
    }

    const payload = buildPayload(values);
    try {
      setLoading(true);

      let res;
      if (isEdit) {
        res = await api_without_cancellation.patch(
          `/api/otherparties/create_update/${getClientId()}/${getCaseId()}/`,
          { id: defendant.id, ...payload }
        );
      } else {
        res = await api_without_cancellation.post(
          `/api/otherparties/create_update/${getClientId()}/${getCaseId()}/`,
          payload
        );
      }
      if (res.status === 200 || res.status === 201) {
        await refetch();
        handleClose();
      }
    } catch (err) {
      console.error("Defendant save failed:", err.response?.data ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  const onInvalid = (errors) => {
    console.warn("Validation errors:", errors);
  };

  // Helpers — put near other constants
  const fmt = (v) => formatPhoneNumber(v) || "";

  const buildEntityFromInsurance = (ins) => ({
    insurance_id: ins?.id ?? null,
    insurance_type_id: ins.insurance_type?.id ?? null,
    liability_limit: ins.liabilityLimit ?? null,
    liability_limit_all: ins.liabilityLimitAll ?? null,
    policy_number: ins.policy_number ?? "",
    claim_number: ins.claim_number ?? "",
    confirmed_date: formatCalendarDateForInput(ins.Dateconfirmedactive) || null,

    company_id: ins?.company?.id ?? null,
    company_dir_id: ins.company?.company_directory ?? null,
    company_name: ins.company_contact?.name ?? "",
    company_address1: ins.company_contact?.address1 ?? "",
    company_address2: ins.company_contact?.address2 ?? "",
    company_city: ins.company_contact?.city ?? "",
    company_state: ins.company_contact?.state ?? "",
    company_zip: ins.company_contact?.zip ?? "",
    company_phone: fmt(ins.company_contact?.phone_number),
    company_extension: ins.company_contact?.phone_ext ?? "",
    company_fax: fmt(ins.company_contact?.fax),
    company_email: ins.company_contact?.email ?? "",
    company_website: ins.company_contact?.website ?? "",

    adjuster_id: ins?.adjuster_ID?.id ?? null,
    adjuster_dir_id: ins?.adjuster_ID?.adjuster_directory ?? null,
    adjuster_first_name: ins.adjuster?.first_name ?? "",
    adjuster_last_name: ins.adjuster?.last_name ?? "",
    adjuster_address1: ins.adjuster?.address1 ?? "",
    adjuster_address2: ins.adjuster?.address2 ?? "",
    adjuster_city: ins.adjuster?.city ?? "",
    adjuster_state: ins.adjuster?.state ?? "",
    adjuster_zip: ins.adjuster?.zip ?? "",
    adjuster_phone: fmt(ins.adjuster?.phone_number),
    adjuster_extension: ins.adjuster?.phone_ext ?? "",
    adjuster_fax: fmt(ins.adjuster?.fax),
    adjuster_email: ins.adjuster?.email ?? "",
    adjuster_website: ins.adjuster?.website ?? "",

    lien_adjuster_id: ins?.lien_adjuster?.id ?? null,
    lien_adjuster_dir_id: ins?.lien_adjuster?.adjuster_directory ?? null,
    lien_adjuster_first_name: ins.lien_adjuster?.contact?.first_name ?? "",
    lien_adjuster_last_name: ins.lien_adjuster?.contact?.last_name ?? "",
    lien_adjuster_address1: ins.lien_adjuster?.contact?.address1 ?? "",
    lien_adjuster_address2: ins.lien_adjuster?.contact?.address2 ?? "",
    lien_adjuster_city: ins.lien_adjuster?.contact?.city ?? "",
    lien_adjuster_state: ins.lien_adjuster?.contact?.state ?? "",
    lien_adjuster_zip: ins.lien_adjuster?.contact?.zip ?? "",
    lien_adjuster_phone: fmt(ins.lien_adjuster?.contact?.phone_number),
    lien_adjuster_extension: ins.lien_adjuster?.contact?.phone_ext ?? "",
    lien_adjuster_fax: fmt(ins.lien_adjuster?.contact?.fax),
    lien_adjuster_email: ins.lien_adjuster?.contact?.email ?? "",
    lien_adjuster_website: ins.lien_adjuster?.contact?.website ?? "",
    total_paid: ins?.insurancelien_insurance?.totalpaid,
    original_lien: ins?.insurancelien_insurance?.liens,
    final_lien: ins?.insurancelien_insurance?.lienfinal,

    supervisor_adjuster_id: ins?.supervisor_ID?.id ?? null,
    supervisor_adjuster_dir_id: ins?.supervisor_ID?.adjuster_directory ?? null,
    supervisor_adjuster_first_name: ins.supervisor?.first_name ?? "",
    supervisor_adjuster_last_name: ins.supervisor?.last_name ?? "",
    supervisor_adjuster_address1: ins.supervisor?.address1 ?? "",
    supervisor_adjuster_address2: ins.supervisor?.address2 ?? "",
    supervisor_adjuster_city: ins.supervisor?.city ?? "",
    supervisor_adjuster_state: ins.supervisor?.state ?? "",
    supervisor_adjuster_zip: ins.supervisor?.zip ?? "",
    supervisor_adjuster_phone: fmt(ins.supervisor?.phone_number),
    supervisor_adjuster_extension: ins.supervisor?.phone_ext ?? "",
    supervisor_adjuster_fax: fmt(ins.supervisor?.fax),
    supervisor_adjuster_email: ins.supervisor?.email ?? "",
    supervisor_adjuster_website: ins.supervisor?.website ?? "",
  });

  const buildEntityFromCounsel = (c) => ({
    counsel_type_id: c.counsel_type?.id ?? null,
    file_number: c.file_number ?? "",
    orignal_lien: c.orignal_lien ?? null,
    final_lien: c.final_lien ?? null,

    opposing_counsel_id: c?.id ?? null,
    counsel_id: c?.firm_directory_record ?? null,
    counsel_name: c.opposingcounselcontact?.name ?? "",
    counsel_address1: c.opposingcounselcontact?.address1 ?? "",
    counsel_address2: c.opposingcounselcontact?.address2 ?? "",
    counsel_city: c.opposingcounselcontact?.city ?? "",
    counsel_state: c.opposingcounselcontact?.state ?? "",
    counsel_zip: c.opposingcounselcontact?.zip ?? "",
    counsel_phone: fmt(c.opposingcounselcontact?.phone_number),
    counsel_extension: c.opposingcounselcontact?.phone_ext ?? "",
    counsel_fax: fmt(c.opposingcounselcontact?.fax),
    counsel_email: c.opposingcounselcontact?.email ?? "",
    counsel_website: c.opposingcounselcontact?.website ?? "",

    attorney_id: c?.attorney_directory_record?.id ?? null,
    attorney_dir_id: c.attorney_directory_record?.directory_id ?? null,
    attorney_first_name: c.opposingattorney?.first_name ?? "",
    attorney_last_name: c.opposingattorney?.last_name ?? "",
    attorney_address1: c.opposingattorney?.address1 ?? "",
    attorney_address2: c.opposingattorney?.address2 ?? "",
    attorney_city: c.opposingattorney?.city ?? "",
    attorney_state: c.opposingattorney?.state ?? "",
    attorney_zip: c.opposingattorney?.zip ?? "",
    attorney_phone: fmt(c.opposingattorney?.phone_number),
    attorney_extension: c.opposingattorney?.phone_ext ?? "",
    attorney_fax: fmt(c.opposingattorney?.fax),
    attorney_email: c.opposingattorney?.email ?? "",
    attorney_website: c.opposingattorney?.website ?? "",

    supervisor_attorney_id: c?.supervisor_directory_record?.id ?? null,
    supervisor_attorney_dir_id: c.supervisor_directory_record?.directory_id ?? null,
    supervisor_attorney_first_name: c.supervisor?.first_name ?? "",
    supervisor_attorney_last_name: c.supervisor?.last_name ?? "",
    supervisor_attorney_address1: c.supervisor?.address1 ?? "",
    supervisor_attorney_address2: c.supervisor?.address2 ?? "",
    supervisor_attorney_city: c.supervisor?.city ?? "",
    supervisor_attorney_state: c.supervisor?.state ?? "",
    supervisor_attorney_zip: c.supervisor?.zip ?? "",
    supervisor_attorney_phone: fmt(c.supervisor?.phone_number),
    supervisor_attorney_extension: c.supervisor?.phone_ext ?? "",
    supervisor_attorney_fax: fmt(c.supervisor?.fax),
    supervisor_attorney_email: c.supervisor?.email ?? "",
    supervisor_attorney_website: c.supervisor?.website ?? "",
  });

  useEffect(() => {
    if (!isEdit) return;
    if (!defendant || Object.keys(defendant).length === 0) return;

    // 1. Build entity arrays with stable UI ids
    const insuranceEntities = (defendant.other_party_insurance ?? []).map((ins) => ({
      id: uid(),
      data: buildEntityFromInsurance(ins),
    }));
    const counselEntities = (defendant.other_party_counsel ?? []).map((c) => ({
      id: 1,
      data: buildEntityFromCounsel(c),
    }));

    // Fall back to a single empty entity when the backend returns none —
    // the UI always needs at least one row per collection.
    const ensureOne = (list, builder) =>
      list.length > 0 ? list : [{ id: uid(), data: builder({}) }];

    const finalInsurance = ensureOne(insuranceEntities, buildEntityFromInsurance);
    const finalCounsel = ensureOne(counselEntities, buildEntityFromCounsel);

    // 2. Set the entity tracking state so third-row tabs render correctly
    setEntities({
      insurance: finalInsurance,
      counsel: finalCounsel,
    });
    setActiveEntityId({
      insurance: finalInsurance[0].id,
      counsel: finalCounsel[0].id,
    });

    // 3. Build RHF defaults — flat defendant fields + namespaced collection maps
    const insuranceMap = Object.fromEntries(finalInsurance.map((e) => [e.id, e.data]));
    const counselMap = Object.fromEntries(finalCounsel.map((e) => [e.id, e.data]));

    reset({
      is_blocked: defendant.is_blocked || false,
      party_type_id: defendant?.party_type_ID?.id,
      ssn: defendant.ssn || "",
      birthday: formatCalendarDateForInput(defendant.party_dob) || null,
      gender: defendant.gender ?? "",
      liability_percent: defendant.liability_percent ?? "",
      liability_estimate: defendant.liability_estimate ?? "",
      repr_letter_sent: formatCalendarDateForInput(defendant.RepLetterSent) || null,
      contact_date: formatCalendarDateForInput(defendant.contact_confirmed_date) || null,
      party_served_date: formatCalendarDateForInput(defendant.party_served_date) || null,
      driver_license: defendant?.driver_license ?? "",
      //   is_blocked: defendant.is_blocked || false,
      //   defendant_type_id: defendant.defendantType || 1,
      //   ssn: defendant.ssn ?? "",
      //   driver_license: defendant.driver_license ?? "",
      //   birthday: formatDate(defendant.birthday),
      //   gender: defendant.gender ?? "",
      //   liability_percent: defendant.liability_percent ?? "",
      //   liability_estimate: defendant.liability_estimate ?? "",
      //   repr_letter_sent: formatDate(defendant.repr_letter_sent),
      //   contact_date: formatDate(defendant.contact_date),
      //   defServedDate: formatDate(defendant.defServedDate),

      first_name: defendant.party_first_name ?? "",
      middle_name: defendant.party_middle_name ?? "",
      last_name: defendant.party_last_name ?? "",
      home_address1: defendant.party_home_contact?.address1 ?? "",
      home_address2: defendant.party_home_contact?.address2 ?? "",
      home_city: defendant.party_home_contact?.city ?? "",
      home_state: defendant.party_home_contact?.state ?? "",
      home_zip: defendant.party_home_contact?.zip ?? "",
      home_phone: fmt(defendant.party_home_contact?.phone_number),
      home_extension: defendant.party_home_contact?.phone_ext ?? "",
      home_fax: fmt(defendant.party_home_contact?.fax),
      home_email: defendant.party_home_contact?.email ?? "",
      home_website: defendant.party_home_contact?.website ?? "",

      party_employer: defendant.party_employer ?? "",
      work_address1: defendant.party_work_contact?.address1 ?? "",
      work_address2: defendant.party_work_contact?.address2 ?? "",
      work_city: defendant.party_work_contact?.city ?? "",
      work_state: defendant.party_work_contact?.state ?? "",
      work_zip: defendant.party_work_contact?.zip ?? "",
      work_phone: fmt(defendant.party_work_contact?.phone_number),
      work_extension: defendant.party_work_contact?.phone_ext ?? "",
      work_fax: fmt(defendant.party_work_contact?.fax),
      work_email: defendant.party_work_contact?.email ?? "",
      work_website: defendant.party_work_contact?.website ?? "",

      // Namespaced collections — keyed by the same UI ids as `entities` state
      insurance: insuranceMap,
      counsel: counselMap,
    });
  }, [isEdit, defendant, reset]);

  return (
    <FormProvider {...methods}>
      <Modal show={show} onHide={handleClose} dialogClassName="modal-dialog-centered modal-800p">
        <div
          className="text-center height-25 p-0 bg-primary modal-header"
          style={{ border: "none" }}
        >
          <div
            className="mx-auto height-25 text-white d-flex align-items-center"
            style={{ fontSize: 14, fontWeight: 600 }}
          >
            {isEdit ? "Edit Party" : "Add Party"}
          </div>
        </div>

        <Modal.Body style={{ padding: 0 }}>
          <form id="addDefForm" onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <div
              style={{
                minHeight: "500px",
              }}
              className="custom-tab m-t-5 m-b-5 defendant-modal-content"
            >
              {!isEdit && (
                <Col md={12} className="d-flex align-items-center justify-content-center">
                  <input
                    type="checkbox"
                    id="dummy_defendant"
                    {...register("is_blocked")}
                    style={{ marginRight: "5px" }}
                  />
                  <label
                    className="mb-0"
                    style={{
                      textTransform: "uppercase",
                      fontWeight: "600",
                    }}
                  >
                    {" "}
                    No Party on Case
                  </label>
                </Col>
              )}

              <TabGrid tabs={FIRST_TABS} selectedId={firstTab} onSelect={setFirstTab} />

              <div className="bg-primary text-center height-25 d-flex align-items-center p-0 m-t-5">
                <h5
                  className="text-white mx-auto"
                  style={{ fontSize: 14, textTransform: "capitalize" }}
                >
                  {FIRST_TABS.find((t) => t.id === firstTab)?.label} View
                </h5>
              </div>

              <TabGrid
                tabs={SECOND_TABS[firstTab]}
                selectedId={secondTab}
                onSelect={setSecondTab}
              />

              {isCollection && (
                <TabGrid
                  tabs={thirdTabs}
                  selectedId={currentEntityId}
                  onSelect={selectEntity}
                  onDelete={deleteEntity}
                  onAdd={addEntity}
                />
              )}

              <FormSection
                key={`${firstTab}-${secondTab}-${currentEntityId ?? "single"}`}
                firstTab={firstTab}
                secondTab={secondTab}
                entityId={currentEntityId}
                statesAbrs={statesAbrs}
              />

              <div
                style={{
                  position: "absolute",
                  bottom: "0px",
                }}
                className="d-flex w-100 justify-content-between custom-margin-top p-b-5 p-l-5 p-r-5"
              >
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  className="height-25"
                  style={{ padding: "0px 12px" }}
                >
                  Cancel
                </Button>
                {isEdit && (
                  <Button
                    variant="danger"
                    onClick={deleteConfirmPopup}
                    className="height-25"
                    style={{ padding: "0px 12px" }}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  variant="success"
                  type="submit"
                  className="m-l-5 bg-success height-25"
                  style={{ padding: "0px 12px" }}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save and Close"}
                </Button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </FormProvider>
  );
};

export default AddOtherPartiesModal;
